import {
  type ColumnDef,
  PaginationState,
  createColumnHelper,
} from "@tanstack/react-table";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import { useClientApplicationSharedCredentialsQuery } from "@graphql/clients/ClientApplicationSharedCredentials.g";

import { Clickable } from "@design-system/Clickable/Clickable";
import { Table } from "@design-system/Table/Table";
import { Text } from "@design-system/Text/Text";

import { routes } from "@pages/routes";

import { usePaginationWithParams } from "@utils/hooks/usePaginationParams";

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_TIME = 400;

type SharedCredentialRow = {
  employeeId: string;
  employeeName: string;
  employeeEmail: string;
  credentialValue: string;
  lastUsed: string;
};

const columnHelper = createColumnHelper<SharedCredentialRow>();

type Props = {
  clientId: string;
  clientApplicationId: string;
  initialPageIndex?: number;
  search?: string;
};

export const ApplicationSharedCredentialsTable = ({
  clientId,
  clientApplicationId,
  initialPageIndex = 0,
  search,
}: Props) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: PAGE_SIZE,
  });
  // Since we fetch credentials, sorting is limited to backend capabilities for credentials, or we sort frontend
  // For now, no sorting backend support defined in query, so we keep sorting empty or simple.
  const [sorting, setSorting] = useState<SortingState>([]);

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_TIME);

  const query = useClientApplicationSharedCredentialsQuery(
    {
      clientId,
      clientApplicationId,
      search: debouncedSearch || undefined,
    },
    {
      enabled: !!clientApplicationId && !!clientId,
    },
  );

  const navigate = useNavigate();
  const handlePaginationChange = usePaginationWithParams(
    pagination,
    setPagination,
  );

  const isLoading = query.isLoading;
  const isFetching = query.isFetching;

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
  }, [debouncedSearch]);

  const data = useMemo(() => {
    if (!query.data?.clientApplication?.credentials?.edges) {
      return [];
    }

    const rows: SharedCredentialRow[] = [];
    
    query.data.clientApplication.credentials.edges.forEach((credEdge) => {
      const cred = credEdge?.node;
      if (!cred) return;

      const sessions = cred.sessions?.edges || [];
      sessions.forEach((sessionEdge) => {
        const session = sessionEdge?.node;
        if (!session || !session.employee) return;

        rows.push({
          employeeId: session.employee.id,
          employeeName: session.employee.name,
          employeeEmail: session.employee.email,
          credentialValue: cred.value,
          lastUsed: session.updatedAt,
        });
      });
    });
    
    // Optional: Sort rows by lastUsed descending by default
    return rows.sort((a, b) => new Date(b.lastUsed).getTime() - new Date(a.lastUsed).getTime());
  }, [query.data]);

  // Frontend pagination
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return data.slice(start, end);
  }, [data, pagination]);

  const totalCount = data.length;

  const handleEmployeeClick = useCallback(
    (clientEmployeeId: string) => {
      navigate(
        routes.portal.clients.client.clientEmployees.clientEmployee.dashboard.$buildPath(
          {
            params: { clientId, clientEmployeeId },
          },
        ),
        {
          viewTransition: true,
        },
      );
    },
    [clientId, navigate],
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("employeeName", {
          header: "Name",
          id: "name",
          cell: (props) => {
            return (
              <Clickable
                onClick={() => handleEmployeeClick(props.row.original.employeeId)}
                outline="sm"
                stretch
              >
                <Table.UserCell name={props.getValue() || ""} />
              </Clickable>
            );
          },
        }),
        columnHelper.accessor("credentialValue", {
          header: "Credential",
          id: "credential",
          cell: (props) => {
            return <Text truncate>{props.getValue() || ""}</Text>;
          },
        }),
        columnHelper.accessor("lastUsed", {
          header: "Last Used",
          id: "lastUsed",
          cell: (props) => {
            return <Table.DateCell date={new Date(props.getValue())} />;
          },
        }),
      ] as ColumnDef<SharedCredentialRow>[],
    [handleEmployeeClick],
  );

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setPagination({ pageIndex: 0, pageSize });
  }, []);

  return (
    <Table
      columns={columns}
      data={paginatedData}
      rowCount={totalCount}
      onPaginationChange={handlePaginationChange}
      pagination={pagination}
      isLoading={isLoading}
      isFetching={isFetching}
      onPageSizeChange={handlePageSizeChange}
      sorting={sorting}
      onSortingChange={setSorting}
    />
  );
};